import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useAuth } from "@/hooks/useAuth";
import { getVipPlans, getSiteSettings } from "@/utils/adminApi";

// ==================== API 层 ====================

interface PaymentConfig {
  alipay_enabled: boolean;
  wechat_enabled: boolean;
  qrcode_enabled: boolean;
  qrcode_priority: boolean;
  alipay_qrcode: string;
  wechat_qrcode: string;
}

async function fetchPaymentConfig(): Promise<PaymentConfig | null> {
  try {
    const token = localStorage.getItem("admin_token") || localStorage.getItem("token");
    const siteId = localStorage.getItem('current_site_id') || '';
    const res = await fetch("/api/admin/settings/payment", {
      method: "GET",
      headers: {
        "Authorization": token ? `Bearer ${token}` : "",
        'X-Site-Id': siteId,
      },
    });
    const data = await res.json();
    if (data.code === 0 && data.data) {
      return {
        alipay_enabled: !!data.data.alipay_enabled,
        wechat_enabled: !!data.data.wechat_enabled,
        qrcode_enabled: !!data.data.qrcode_enabled,
        qrcode_priority: !!data.data.qrcode_priority,
        alipay_qrcode: data.data.alipay_qrcode || "",
        wechat_qrcode: data.data.wechat_qrcode || "",
      };
    }
    return null;
  } catch (e) {
    console.error("❌ VIP: 读取支付配置失败", e);
    return null;
  }
}

// ==================== 类型 ====================

interface VipPlan {
  id: string;
  name: string;
  price: number;
  originalPrice: number;
  duration: string;
  badge: string;
  features: string[];
  duration_months: number;
}

const DEFAULT_VIP_PLANS: VipPlan[] = [
  {
    id: "monthly",
    name: "月度会员",
    price: 29,
    originalPrice: 49,
    duration: "1个月",
    badge: "",
    features: [
      "信息置顶展示 3次/月",
      "发布信息不限条数",
      "专属会员标识",
      "优先客服支持",
    ],
    duration_months: 1,
  },
  {
    id: "quarterly",
    name: "季度会员",
    price: 79,
    originalPrice: 147,
    duration: "3个月",
    badge: "热门",
    features: [
      "信息置顶展示 10次/季",
      "发布信息不限条数",
      "专属会员标识",
      "优先客服支持",
      "数据统计分析",
    ],
    duration_months: 3,
  },
  {
    id: "yearly",
    name: "年度会员",
    price: 199,
    originalPrice: 588,
    duration: "12个月",
    badge: "超值",
    features: [
      "信息置顶展示 不限次数",
      "发布信息不限条数",
      "专属会员标识",
      "专属客服支持",
      "数据统计分析",
      "优先审核通道",
      "品牌推广曝光",
    ],
    duration_months: 12,
  },
];

const VIP_BENEFITS = [
  { icon: "ri-rocket-line", title: "置顶曝光", desc: "信息优先展示在列表顶部，获得更多浏览量" },
  { icon: "ri-shield-check-line", title: "认证标识", desc: "专属VIP认证标识，提升企业可信度" },
  { icon: "ri-bar-chart-line", title: "数据分析", desc: "查看信息浏览、联系等详细数据统计" },
  { icon: "ri-customer-service-line", title: "专属客服", desc: "优先响应，专属客服一对一服务" },
  { icon: "ri-infinity-line", title: "无限发布", desc: "不限条数发布服务信息，无需担心限制" },
  { icon: "ri-speed-line", title: "极速审核", desc: "信息优先审核，快速上线展示" },
];

// ==================== 页面组件 ====================

export default function VipPage() {
  const { isLoggedIn } = useAuth();
  const [vipPlans, setVipPlans] = useState<VipPlan[]>(DEFAULT_VIP_PLANS);
  const [selectedPlan, setSelectedPlan] = useState("quarterly");
  const [payMethod, setPayMethod] = useState<"wechat" | "alipay" | "qrcode">("wechat");
  const [showPayModal, setShowPayModal] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState<PaymentConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [enableVip, setEnableVip] = useState(true);

  // 加载数据
  useEffect(() => {
    async function loadData() {
      try {
        // 并行加载
        const [plans, settings, payments] = await Promise.all([
          getVipPlans().catch(() => null),
          getSiteSettings().catch(() => null),
          fetchPaymentConfig(),
        ]);

        // VIP功能开关
        if (settings) {
          setEnableVip(settings.enable_vip !== false);
        }

        // 处理套餐数据
        if (plans && Array.isArray(plans) && plans.length > 0) {
          const formattedPlans = plans.map((p: any) => ({
            id: p.id,
            name: p.name || "VIP套餐",
            price: p.amount || p.price || 0,
            originalPrice: p.original_price || p.originalPrice || 0,
            duration: `${p.duration_months || p.duration || 1}个月`,
            badge: p.is_recommended ? "推荐" : "",
            features: p.benefits || p.features || [],
            duration_months: p.duration_months || 1,
          }));
          setVipPlans(formattedPlans);
          if (formattedPlans.length > 0) {
            setSelectedPlan(formattedPlans[0].id);
          }
        }

        // 处理支付配置
        if (payments) {
          console.log("✅ VIP: 加载支付配置成功", payments);
          setPaymentConfig(payments);
          // 如果收款码优先且启用，默认选中收款码
          if (payments.qrcode_enabled && payments.qrcode_priority) {
            setPayMethod("qrcode");
          }
        } else {
          console.log("❌ VIP: 无支付配置");
        }
      } catch (e) {
        console.error("加载VIP数据失败:", e);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const currentPlan = vipPlans.find((p) => p.id === selectedPlan);

  // 判断支付方式：收款码模式看 qrcode_enabled，API模式看 alipay_enabled/wechat_enabled
  const showAlipay = !!paymentConfig?.alipay_qrcode && (!!paymentConfig?.qrcode_enabled || !!paymentConfig?.alipay_enabled);
  const showWechat = !!paymentConfig?.wechat_qrcode && (!!paymentConfig?.qrcode_enabled || !!paymentConfig?.wechat_enabled);
  const showQrcode = showAlipay && showWechat;

  // 如果当前选中的支付方式不可用，自动切换
  useEffect(() => {
    if (payMethod === "wechat" && !showWechat) {
      setPayMethod(showAlipay ? "alipay" : "qrcode");
    } else if (payMethod === "alipay" && !showAlipay) {
      setPayMethod(showWechat ? "wechat" : "qrcode");
    } else if (payMethod === "qrcode" && !showQrcode) {
      setPayMethod(showWechat ? "wechat" : "alipay");
    }
  }, [showWechat, showAlipay, showQrcode]);

  const handlePay = () => {
    if (!isLoggedIn) {
      window.location.href = "/login?redirect=/vip";
      return;
    }
    setShowPayModal(true);
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Navbar />
      <main className="pt-20">
        {/* VIP功能已关闭提示 */}
        {!enableVip && !loading && (
          <div className="w-full px-4 md:px-8 py-16 md:py-24">
            <div className="max-w-md mx-auto text-center">
              <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-gray-100">
                <i className="ri-vip-crown-line text-4xl text-gray-400" />
              </div>
              <h1 className="text-xl font-bold text-gray-800 mb-3">VIP会员功能已关闭</h1>
              <p className="text-gray-500 mb-6">非常抱歉，VIP会员功能目前暂时关闭，如有需要请联系管理员。</p>
              <Link to="/dashboard" className="inline-flex items-center gap-2 px-6 py-2.5 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-colors">
                <i className="ri-arrow-left-line" />
                返回会员中心
              </Link>
            </div>
          </div>
        )}

        {/* Hero */}
        {enableVip && (
        <>
        <div className="bg-gradient-to-br from-orange-500 via-orange-400 to-amber-400 text-white">
          <div className="w-full px-4 md:px-8 py-12 md:py-16">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/20 rounded-full text-sm mb-4">
                <i className="ri-vip-crown-line" />
                <span>VIP 会员</span>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold mb-3">开通VIP，让您的服务被更多人看见</h1>
              <p className="text-white/80 text-base md:text-lg">置顶曝光 · 无限发布 · 专属认证 · 数据分析</p>
            </div>
          </div>
        </div>

        {/* Benefits */}
        <div className="w-full px-4 md:px-8 py-10 md:py-14">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-gray-800 text-center mb-8">VIP 专属权益</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
              {VIP_BENEFITS.map((b) => (
                <div key={b.title} className="bg-white rounded-2xl p-5 border border-gray-100 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-xl bg-orange-50 text-orange-500">
                    <i className={`${b.icon} text-2xl`} />
                  </div>
                  <h3 className="font-semibold text-gray-800 mb-1.5">{b.title}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Pricing */}
        <div className="w-full px-4 md:px-8 pb-12 md:pb-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-gray-800 text-center mb-8">选择套餐</h2>
            {loading ? (
              <div className="text-center py-12">
                <div className="w-10 h-10 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-500">加载中...</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  {vipPlans.map((plan) => (
                    <div
                      key={plan.id}
                      onClick={() => setSelectedPlan(plan.id)}
                      className={`relative cursor-pointer rounded-2xl border-2 p-5 transition-all duration-200 ${
                        selectedPlan === plan.id
                          ? "border-orange-500 bg-orange-50"
                          : "border-gray-200 bg-white hover:border-orange-300"
                      }`}
                    >
                      {plan.badge && (
                        <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-orange-500 text-white text-xs rounded-full whitespace-nowrap">
                          {plan.badge}
                        </span>
                      )}
                      <div className="text-center mb-4">
                        <h3 className="font-bold text-gray-800 text-base mb-1">{plan.name}</h3>
                        <div className="flex items-baseline justify-center gap-1">
                          <span className="text-3xl font-bold text-orange-500">¥{plan.price}</span>
                          <span className="text-sm text-gray-400">/{plan.duration}</span>
                        </div>
                        {plan.originalPrice > plan.price && (
                          <p className="text-xs text-gray-400 mt-1 line-through">原价 ¥{plan.originalPrice}</p>
                        )}
                      </div>
                      <ul className="space-y-2">
                        {plan.features.map((f, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                            <i className="ri-check-line text-orange-500 shrink-0" />
                            {f}
                          </li>
                        ))}
                      </ul>
                      {selectedPlan === plan.id && (
                        <div className="absolute top-3 right-3 w-5 h-5 flex items-center justify-center rounded-full bg-orange-500">
                          <i className="ri-check-line text-white text-xs" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>

            {/* Pay Method */}
            <div className="bg-white rounded-2xl p-5 border border-gray-100 mb-6">
              <h3 className="text-sm font-semibold text-gray-800 mb-3">支付方式</h3>
              <div className="flex gap-3 flex-wrap">
                {showQrcode && (
                  <button
                    onClick={() => setPayMethod("qrcode")}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 text-sm transition-all ${
                      payMethod === "qrcode"
                        ? "border-purple-500 bg-purple-50 text-purple-700"
                        : "border-gray-200 text-gray-600 hover:border-gray-300"
                    }`}
                  >
                    <i className="ri-qr-code-line text-lg" />
                    收款码支付
                  </button>
                )}
                {showWechat && (
                  <button
                    onClick={() => setPayMethod("wechat")}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 text-sm transition-all ${
                      payMethod === "wechat"
                        ? "border-green-500 bg-green-50 text-green-700"
                        : "border-gray-200 text-gray-600 hover:border-gray-300"
                    }`}
                  >
                    <i className="ri-wechat-pay-fill text-lg" />
                    微信支付
                  </button>
                )}
                {showAlipay && (
                  <button
                    onClick={() => setPayMethod("alipay")}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 text-sm transition-all ${
                      payMethod === "alipay"
                        ? "border-orange-400 bg-orange-50 text-orange-700"
                        : "border-gray-200 text-gray-600 hover:border-gray-300"
                    }`}
                  >
                    <i className="ri-alipay-fill text-lg" />
                    支付宝
                  </button>
                )}
              </div>
            </div>
            </>
            )}
            {/* CTA */}
            <div className="text-center">
              <button
                onClick={handlePay}
                className="inline-flex items-center gap-2 px-10 py-4 bg-orange-500 text-white font-semibold rounded-2xl hover:bg-orange-600 transition-colors text-base"
              >
                <i className="ri-vip-crown-line" />
                立即开通 {currentPlan?.name} — ¥{currentPlan?.price}
              </button>

            </div>
          </div>
        </div>
        </>
        )}
      </main>
      {enableVip && <Footer />}

      {/* Pay Modal */}
      {enableVip && showPayModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-800">扫码支付</h3>
              <button onClick={() => setShowPayModal(false)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors">
                <i className="ri-close-line text-gray-500" />
              </button>
            </div>
            <div className="text-center">
              {/* 支付宝收款码 */}
              {payMethod === "alipay" && showAlipay && (
                <div>
                  <p className="text-xs text-gray-500 mb-2">请使用支付宝扫码付款</p>
                  <img src={paymentConfig?.alipay_qrcode} alt="支付宝收款码" className="w-48 h-48 mx-auto rounded-xl border border-gray-200" />
                </div>
              )}
              {/* 微信收款码 */}
              {payMethod === "wechat" && showWechat && (
                <div>
                  <p className="text-xs text-gray-500 mb-2">请使用微信扫码付款</p>
                  <img src={paymentConfig?.wechat_qrcode} alt="微信收款码" className="w-48 h-48 mx-auto rounded-xl border border-gray-200" />
                </div>
              )}
              {/* 收款码模式 */}
              {payMethod === "qrcode" && (
                <div className="flex flex-col items-center gap-3">
                  {showAlipay && (
                    <div>
                      <p className="text-xs text-gray-500 mb-2">支付宝扫码付款</p>
                      <img src={paymentConfig?.alipay_qrcode} alt="支付宝收款码" className="w-40 h-40 mx-auto rounded-xl border border-gray-200" />
                    </div>
                  )}
                  {showWechat && (
                    <div>
                      <p className="text-xs text-gray-500 mb-2">微信扫码付款</p>
                      <img src={paymentConfig?.wechat_qrcode} alt="微信收款码" className="w-40 h-40 mx-auto rounded-xl border border-gray-200" />
                    </div>
                  )}
                </div>
              )}
              {/* 无收款码占位 */}
              {!showAlipay && !showWechat && (
                <>
                  <div className="w-40 h-40 mx-auto mb-4 bg-gray-100 rounded-xl flex items-center justify-center">
                    <i className="ri-qr-code-line text-5xl text-gray-300" />
                  </div>
                  <p className="text-sm text-gray-600 mb-1">支付功能暂未启用</p>
                </>
              )}
              <p className="text-2xl font-bold text-orange-500 mb-1">¥{currentPlan?.price}</p>
              <p className="text-xs text-gray-400">{currentPlan?.name} · {currentPlan?.duration}</p>
            </div>
            <div className="mt-5 p-3 bg-orange-50 rounded-xl text-xs text-orange-700 text-center">
              支付成功后将自动开通VIP权益
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
